import type { AppContext } from 'types/common'
import type { ComponentProps } from 'svelte'

import { Dashboard } from '@isoftdata/svelte-dashboard'
import { checkSessionPermission } from 'utility/permission'
import {
	loadChartDataForReport,
	loadDashboardReportMetadata,
	loadDashboardReportTitlesForUser,
} from 'utility/dashboard-helper'
import { getSession } from 'stores/session'

export default function createState({ mediator, stateRouter }: AppContext) {
	stateRouter.addState({
		name: 'app.dashboard',
		route: 'dashboard/:dashboardReportId',
		querystringParameters: ['dashboardReportId'],
		defaultParameters: { dashboardReportId: null },
		template: {
			svelte: true,
			component: Dashboard,
		},
		async resolve(data, parameters): Promise<ComponentProps<typeof Dashboard>> {
			if (!checkSessionPermission('DASHBOARD_REPORT_VIEW')) {
				console.error("You don't have permission to view the dashboard.")
				throw {
					redirectTo: {
						name: 'app',
						params: {},
					},
				}
			}

			// Don't use cache here, just in case we just added a new report
			// But, when the dashboard component calls this function, it'll use the cached value from here
			const dashboardReports = await loadDashboardReportTitlesForUser(false)
			const dashboardReportId: number | null = parseInt(parameters.dashboardReportId, 10)
			const isValidReport =
				!!dashboardReportId && dashboardReports.some(report => report?.dashboardReportId === dashboardReportId)

			if (!isValidReport && (dashboardReports.length || !!dashboardReportId)) {
				throw {
					redirectTo: {
						name: null,
						params: { dashboardReportId: dashboardReports[0].dashboardReportId },
					},
				}
			}

			const session = getSession()
			return Promise.resolve({
				selectedReportId: parameters.dashboardReportId ? parseInt(parameters.dashboardReportId, 10) : null,
				currentUserAccountId: session.userAccountId,
				canEditAll: checkSessionPermission('DASHBOARD_REPORT_EDIT_ALL'),
				canEditOwn: checkSessionPermission('DASHBOARD_REPORT_EDIT_OWN'),
				configureReportClicked: dashboardReportId =>
					stateRouter.go('app.configuration.dashboard.report', { dashboardReportId }),
				reportClicked: (dashboardReportId: number) => stateRouter.go(null, { dashboardReportId }),
				loadChartDataForReport,
				loadDashboardReportMetadata,
				loadDashboardReportTitlesForUser,
			})
		},
	})
}
